import { CheckCircleFilled } from '@ant-design/icons';
import React, { useState } from 'react';
import Carousel from 'react-multi-carousel';
import { useSelector } from 'react-redux';
import { rupiah } from 'src/actions/rupiah';
import useApi from 'src/actions/useApi';
import { API_MES, URL_MES_COURSE } from 'src/constants';
import { LoadingText } from 'src/web/components/Loading/LoadingSpinner';
import { trackEvent } from 'src/web/Utils/trackEvent';
import styled, { css } from 'styled-components';

/* ---------------- Styled Components ---------------- */

const ACCENT = '#FF0F51';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  scroll-margin-top: 80px;
`;

const Wrapper = styled.div`
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f3f4f6;
  padding: 24px;
`;

const Title = styled.h2`
  margin-bottom: 8px;
  font-size: 1.5rem;
  font-weight: 700;
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  cursor: pointer;
  user-select: none;

  border-radius: 16px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  background: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: ${ACCENT};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  ${(props) =>
    props.selected &&
    css`
      border-color: ${ACCENT};
      border-bottom-width: 8px;
      background: #ffe3ec; /* soft accent background */
      &:hover {
        border-color: ${ACCENT};
      }
    `}
`;

const CardTitle = styled.h3`
  margin-bottom: 4px;
  font-size: 1.125rem;
  font-weight: 600;
`;

const CardDuration = styled.p`
  margin-bottom: 8px;
  font-weight: 500;
  color: ${ACCENT};
`;

const Price = styled.p`
  margin-bottom: 16px;
  font-size: 1.125rem;
  font-weight: 700;
`;

const BenefitList = styled.ul`
  margin-bottom: 16px;
  list-style: none;
  padding: 0;

  li {
    display: flex;
    align-items: start;
    gap: 8px;
    font-size: 0.875rem;
    color: #374151;
  }
`;

const Button = styled.button`
  margin-top: 16px;
  width: 100%;
  padding: 8px;
  font-size: 0.875rem;
  border-radius: 6px;
  border: 1px solid ${ACCENT};
  transition: all 0.2s ease;
  cursor: pointer;

  ${(props) =>
    props.selected
      ? css`
          background: ${ACCENT};
          color: white;
        `
      : css`
          background: white;
          color: ${ACCENT};
          &:hover {
            background: #ffe3ec;
          }
        `}
`;

const InfoBox = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: #fff7fa;
  text-align: center;
`;

const InfoText = styled.p`
  margin-bottom: 12px;
  color: #374151;
  font-size: 0.95rem;
`;

const GoCoursesButton = styled.a`
  display: inline-block;
  padding: 10px 20px;
  border-radius: 8px;
  background: ${ACCENT};
  color: white !important;
  font-weight: 600;
  text-decoration: none;
  transition: 0.2s ease-in-out;

  &:hover {
    background: #e20d48;
    color: white !important;
  }
`;

/* ---------------- Component Logic ---------------- */

export default function SubscriptionOptions() {
  const userData = useSelector((state) => state.profile_data);

  const isActiveSub = userData?.mes_subscription?.is_active;

  const packagesApi = useApi({
    url: `${API_MES}/front/mesx/subscription/packages`,
  });

  const packages = packagesApi?.data ?? [];
  const [selected, setSelected] = useState('');

  const handleSubscribe = ({ pkg }) => {
    setSelected(pkg);
    window.open(`${URL_MES_COURSE}/checkout?mesx_subscription_id=${pkg?.id}`);

    trackEvent({
      eventName: 'buy_package',
      params: {
        package_name: pkg?.name ?? '',
      },
    });
  };

  return (
    <Container id="subscription">
      {!isActiveSub && (
        <Wrapper>
          <Title>Pilih Paket Belajar yang Cocok Buat Kamu</Title>

          {packagesApi?.isLoading && (
            <LoadingText>
              Loading<span>.</span>
              <span>.</span>
              <span>.</span>
            </LoadingText>
          )}

          <Carousel
            responsive={{
              desktop: {
                breakpoint: { max: 3000, min: 1024 },
                items: 2,
                partialVisibilityGutter: 20,
              },
              tablet: {
                breakpoint: { max: 1024, min: 640 },
                items: 2,
                partialVisibilityGutter: 15,
              },
              mobile: { breakpoint: { max: 640, min: 0 }, items: 1, partialVisibilityGutter: 10 },
            }}
            infinite={false}
            arrows={true}
            draggable
            keyBoardControl
            itemClass="px-2"
            containerClass="pb-4"
            partialVisbile
          >
            {packages?.map((pkg) => {
              const isSelected = selected?.id === pkg?.id;
              return (
                <Card key={pkg?.id} selected={isSelected}>
                  <div>
                    <CardTitle>{pkg?.name}</CardTitle>
                    <CardDuration>{pkg?.duration_in_month} Bulan</CardDuration>
                    <Price>{rupiah(pkg?.price ?? 0)}</Price>

                    <BenefitList>
                      {pkg?.description_array.map((b, i) => (
                        <li key={i}>
                          <CheckCircleFilled style={{ color: ACCENT, fontSize: 18 }} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </BenefitList>
                  </div>

                  <Button selected={isSelected} onClick={() => handleSubscribe({ pkg })}>
                    {isSelected ? 'Dipilih' : 'Beli Paket'}
                  </Button>
                </Card>
              );
            })}
          </Carousel>
        </Wrapper>
      )}

      <InfoBox>
        <InfoText>
          {isActiveSub
            ? 'Kamu sudah berlangganan! Yuk langsung eksplor semua kelas yang tersedia.'
            : 'Atau kamu juga bisa beli per kelas sesuai kebutuhanmu'}
        </InfoText>

        <GoCoursesButton
          href="/courses"
          onClick={() => {
            trackEvent({
              eventName: 'button_click_lihat_semua_kelas',
              params: { item_category: 'mesx' },
            });
          }}
        >
          Lihat Semua Kelas
        </GoCoursesButton>
      </InfoBox>
    </Container>
  );
}
